import axios from 'axios';

import { tokenService } from '../auth/tokenService';

export const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

const clientConfig = {
  baseURL: API_BASE_URL,
  withCredentials: true,
  withXSRFToken: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
  headers: {
    Accept: 'application/json',
  },
};

export const authClient = axios.create(clientConfig);
const apiClient = axios.create(clientConfig);

let refreshTokenRequest = null;
const authFailureListeners = new Set();

export function onAuthFailure(listener) {
  authFailureListeners.add(listener);
  return () => authFailureListeners.delete(listener);
}

function notifyAuthFailure() {
  authFailureListeners.forEach((listener) => listener());
}

function isAuthEndpoint(url = '') {
  return url.includes('/api/token/');
}

export function refreshAccessToken() {
  const generation = tokenService.getSessionGeneration();
  if (!tokenService.canRefresh()) {
    return Promise.reject(new Error('Authentication session has ended. Sign in again.'));
  }
  if (!refreshTokenRequest || refreshTokenRequest.generation !== generation) {
    const promise = authClient
      .post('/api/token/refresh/', {})
      .then((response) => {
        const access = response.data?.access;
        if (!access) {
          throw new Error('Token refresh response did not include an access token.');
        }
        if (!tokenService.setAccessToken(access, generation)) {
          throw new Error('Authentication session has changed. Sign in again.');
        }
        return access;
      })
      .finally(() => {
        if (refreshTokenRequest?.promise === promise) refreshTokenRequest = null;
      });
    refreshTokenRequest = { generation, promise };
  }
  return refreshTokenRequest.promise;
}

// A stale refresh response can delete an HttpOnly cookie even when its token
// is ignored. Finish it before a subsequent login sets a fresh cookie.
export async function waitForPendingRefresh() {
  try {
    await refreshTokenRequest?.promise;
  } catch (_) {
    // The caller is explicitly starting a new login.
  }
}

apiClient.interceptors.request.use((config) => {
  const generation = tokenService.getSessionGeneration();
  if (config._authGeneration != null && config._authGeneration !== generation) {
    throw new Error('Authentication session has changed. Sign in again.');
  }
  config._authGeneration = generation;
  const accessToken = tokenService.getAccessToken();
  if (accessToken && !isAuthEndpoint(config.url)) {
    config.headers.Authorization = 'Bearer ' + accessToken;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const generation = originalRequest?._authGeneration;

    if (!originalRequest || status !== 401 || isAuthEndpoint(originalRequest.url) ||
        generation !== tokenService.getSessionGeneration() || !tokenService.canRefresh()) {
      return Promise.reject(error);
    }
    if (originalRequest._retry) {
      tokenService.clearAccessToken();
      notifyAuthFailure();
      return Promise.reject(error);
    }
    originalRequest._retry = true;
    try {
      const access = await refreshAccessToken();
      if (generation !== tokenService.getSessionGeneration()) throw new Error('Authentication session has changed.');
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = 'Bearer ' + access;
      return apiClient(originalRequest);
    } catch (refreshError) {
      // An old request must neither restore an ended session nor clear a new one.
      if (generation === tokenService.getSessionGeneration()) {
        tokenService.clearAccessToken();
        notifyAuthFailure();
      }
      return Promise.reject(refreshError);
    }
  }
);

export function getApiErrorMessage(error, fallback = 'Request failed') {
  const data = error.response?.data;
  const detail = data?.detail || data?.error || data?.message;

  if (Array.isArray(detail)) {
    return detail.join(', ');
  }
  if (detail) {
    return String(detail);
  }
  if (error.message) {
    return error.message;
  }
  return fallback;
}

export default apiClient;
